import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import * as pt_br from 'dayjs/locale/pt-br';
import weekday from 'dayjs/plugin/weekday';

dayjs.locale(pt_br);
dayjs.extend(utc);
dayjs.extend(customParseFormat);
dayjs.extend(weekday);

export const formatBRDate = (
  BRdate: string,
  { formatDate = 'YYYY-MM-DD', epoch = false } = {}
) => {
  if (!BRdate) return null;
  if (epoch) return dayjs(BRdate, 'DD/MM/YYYY').valueOf();

  return dayjs(BRdate, 'DD/MM/YYYY').format(formatDate);
};

export const getUtcDate = (
  epoch: string | number,
  formatDate = 'DD-MM-YYYY'
): string => {
  let epochNumber = epoch;

  if (typeof epoch === 'string') {
    epochNumber = Number(epoch);
  }

  return dayjs(epochNumber).utc().format(formatDate);
};

export const arrayWithLast12Months = (): Date[] => {
  const dates = [...Array(12)].map((item, index) =>
    dayjs.utc().subtract(index, 'month').toDate()
  );

  return dates;
};

export const getYear = (date: Date | number | Dayjs, applyUTC = false) => {
  if (applyUTC) {
    return dayjs(date).utc().get('year');
  }

  return dayjs(date).get('year');
};

export const getMonth = (date: Date | number | Dayjs, applyUTC = false) => {
  if (applyUTC) {
    return dayjs(date).utc().get('month') + 1;
  }

  return dayjs(date).get('month') + 1;
};

export const getRelativeEpoch = (
  periodToSubtract: number,
  lastDateEpoch?: number
): number => {
  return dayjs(lastDateEpoch)
    .subtract(periodToSubtract, 'month')
    .add(1, 'day')
    .valueOf();
};

export const getUtcEpoch = (date: Date | number | Dayjs) => {
  return dayjs.utc(date).valueOf();
};

interface IGetDateWithoutTimezone {
  type?: 'epoch' | 'date' | 'format';
  formatDate?: string;
}

export const getDateWithoutTimezone = (
  date: number | string,
  { type = 'epoch', formatDate = 'DD-MM-YYYY' }: IGetDateWithoutTimezone = {},
  defaultDate?: number | string
) => {
  if (!date) return defaultDate;

  const dayjsDate = dayjs(date)
    .set('hour', 0)
    .set('minute', 0)
    .set('second', 0)
    .add(dayjs(date).utcOffset(), 'minute');

  switch (type) {
    case 'epoch':
      return dayjsDate.valueOf();
    case 'date':
      return dayjsDate.toDate();
    case 'format':
      return dayjsDate.format(formatDate);
    default:
      return dayjsDate.toDate();
  }
};

export const isValidYear = (year) => {
  return dayjs().year(year).isValid();
};

export function getTheLastWorkingDays({
  daysAgo = 1,
  sinceDate,
}: {
  daysAgo?: number;
  sinceDate?: string | Date | dayjs.Dayjs;
}) {
  let date = sinceDate ?? dayjs();
  let workingDays = 0;
  const daysForSubtract = daysAgo;

  while (workingDays < daysForSubtract) {
    date = dayjs(date).subtract(1, 'day');
    checkIfIsWorkingDay(date.toDate()) && workingDays++;
  }

  return dayjs(date);
}

export function checkIfIsWorkingDay(date: string | Date | dayjs.Dayjs) {
  if (dayjs(date).weekday() === 0 || dayjs(date).weekday() === 6) {
    return false;
  }

  return true;
}

export const dayjsInstance = dayjs;

export const isDate = (date: string) => {
  if (date?.length < 5) return false;

  return dayjs(date).isValid();
};
